import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom"; // Use useLocation to access query params
import invoiceService from "../services/invoiceService"; // Use invoiceService
import associationService from "../services/associationService";
import '../css/InvoiceDetails.css';
import ToggleReviewButton from "../components/invoicecomponents/ToggleReviewButton"; // Import ToggleReviewButton
import CLIENT_PORTAL_URLS from '../config/clientPortalUrls';
import EmailListView from "../components/EmailListView"; // Import EmailListView
import Cookies from 'js-cookie';  // Add this import for the auth token
import DuplicateSourceBanner from "../components/DuplicateSourceBanner";

// Helper to make links clickable in comments
function linkify(text) {
    if (!text) return '';
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
    return text.replace(urlRegex, function(url) {
        let href = url;
        if (!href.match(/^https?:\/\//)) {
            href = 'http://' + href;
        }
        return `<a href="${href}" target="_blank" rel="noopener noreferrer">${url}</a>`;
    });
}

const InvoiceDetails = () => {
    const location = useLocation(); // Access the current location
    const navigate = useNavigate();
    const [invoice, setInvoice] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({});
    const [errorMessage, setErrorMessage] = useState("");
    const [commentText, setCommentText] = useState("");
    const [isEditingComment, setIsEditingComment] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [showEmailInfo, setShowEmailInfo] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [associatedEmail, setAssociatedEmail] = useState(null);

    // --- Handlers to fix no-undef errors ---
    // Handles comment update
    // --- Real handler implementations for editing and saving ---
    const handleCommentUpdate = async () => {
        try {
            const searchParams = new URLSearchParams(location.search);
            const id = searchParams.get("id");
            const updatedInvoice = await invoiceService.updateComment(id, commentText);
            setInvoice(updatedInvoice);
            setIsEditingComment(false);
        } catch (error) {
            setErrorMessage("Failed to update comment. Please try again.");
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        try {
            const searchParams = new URLSearchParams(location.search);
            const id = searchParams.get("id");
            const updatedInvoice = await invoiceService.updateInvoice(id, formData);
            setInvoice(updatedInvoice);
            setIsEditing(false);
        } catch (error) {
            setErrorMessage("Error updating invoice. Please check the server logs for more details.");
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleLineItemChange = (index, field, value) => {
        setFormData((prev) => {
            const items = prev.lineItems ? [...prev.lineItems] : [];
            items[index] = { ...items[index], [field]: value };
            return { ...prev, lineItems: items };
        });
    };

    const handleRemoveLineItem = (index) => {
        setFormData((prev) => {
            const items = prev.lineItems ? [...prev.lineItems] : [];
            items.splice(index, 1);
            return { ...prev, lineItems: items };
        });
    };

    const handleAddLineItem = () => {
        setFormData((prev) => ({
            ...prev,
            lineItems: prev.lineItems ? [...prev.lineItems, { property: '', unit: '', glAccount: '', description: '', amount: '' }] : [{ property: '', unit: '', glAccount: '', description: '', amount: '' }]
        }));
    };
      // Function to open AppFolio URL for this invoice
    const openAppFolioUrl = () => {
        // Default URL (fallback)
        const defaultUrl = 'https://appfolio.com/login';
        
        // First check if the invoice has its own appfolioUrl
        if (invoice && invoice.appfolioUrl) {
            window.open(invoice.appfolioUrl, '_blank', 'noopener,noreferrer');
        }
        // Otherwise check if we have a client ID to get the URL
        else if (invoice && invoice.client_id) {
            fetch(`${process.env.REACT_APP_API_URL || ''}/api/clients/${invoice.client_id}`, {
                headers: {
                    'Authorization': `Bearer ${Cookies.get('token')}`
                }
            })
            .then(response => response.json())
            .then(data => {
                if (data.success && data.client && data.client.appfolioUrl) {
                    window.open(data.client.appfolioUrl, '_blank', 'noopener,noreferrer');
                } else {
                    window.open(defaultUrl, '_blank', 'noopener,noreferrer');
                }
            })
            .catch(err => {
                console.error("Error fetching client:", err);
                window.open(defaultUrl, '_blank', 'noopener,noreferrer');
            });
        } else {
            // No client ID, just use default URL
            window.open(defaultUrl, '_blank', 'noopener,noreferrer');
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            console.log(`📋 Copied to clipboard: ${text}`);
        }).catch((error) => {
            console.error("❌ Failed to copy to clipboard:", error);
        });
    };    const handleGoBack = () => {
        // Navigate back to the members page with the invoices view
        // This ensures consistent navigation rather than relying on browser history
        navigate('/members');
    };

    useEffect(() => {
        const fetchInvoice = async () => {
            const searchParams = new URLSearchParams(location.search);
            const id = searchParams.get("id"); // Get the invoice ID from query params
            if (!id) {
                setErrorMessage("Invoice ID is missing.");
                return;
            }
            try {
                const data = await invoiceService.getInvoiceById(id);
                if (!data) {
                    setErrorMessage("Invoice not found.");
                } else {
                    setInvoice(data);
                    setFormData(data);
                }
            } catch (error) {
                setErrorMessage("Error fetching invoice details.");
            }
        };
        fetchInvoice();
    }, [location.search]);
    // eslint-disable-next-line no-unused-vars
    const openAssociatedEmailTab = async () => {
        console.log('🔍 openAssociatedEmailTab: Function called');
        
        // Check if we have an invoice with emailId
        if (!invoice) {
            console.error('❌ openAssociatedEmailTab: No invoice available');
            alert('No invoice data available.');
            return;
        }
        
        if (!invoice.emailId) {
            console.error('❌ openAssociatedEmailTab: No emailId in invoice', invoice);
            alert('No associated email found for this invoice.');
            return;
        }
        
        console.log(`📧 openAssociatedEmailTab: Found emailId: ${invoice.emailId}`);
        
        // Simplified approach - directly open tab with the URL
        try {
            // Direct opening to avoid the need for prefetching the email
            console.log(`📧 Opening tab directly for email ID: ${invoice.emailId}`);
            const baseUrl = window.location.origin; // Gets the base URL of the current site
            const emailUrl = `${baseUrl}/email/${invoice.emailId}`;
            console.log(`📧 Full URL: ${emailUrl}`);
            
            // Open the tab directly
            const newTabResult = window.open(emailUrl, '_blank');
            
            if (!newTabResult || newTabResult.closed || typeof newTabResult.closed === 'undefined') {
                console.error('❌ Popup seems to be blocked');
                alert('Popup blocked! Please allow popups for this site.');
            } else {
                console.log('✅ Tab opened successfully');
            }
        } catch (error) {
            console.error('❌ Error opening email tab:', error);
            alert('Error opening associated email tab: ' + error.message);
        }
    };    // Test function to check browser environment and email data
    // eslint-disable-next-line no-unused-vars
    const runDiagnostics = () => {
        const diagnosticInfo = {
            console: typeof console !== 'undefined',
            alert: typeof alert !== 'undefined',
            window: typeof window !== 'undefined',
            windowOpen: typeof window !== 'undefined' && typeof window.open === 'function',
            location: typeof window !== 'undefined' && typeof window.location !== 'undefined',
            origin: typeof window !== 'undefined' && window.location ? window.location.origin : 'unknown',
            invoice: invoice ? 'available' : 'not available',
            emailId: invoice?.emailId ? invoice.emailId : 'not available',
            cookies: document.cookie.length > 0 ? 'available' : 'not available'
        };
        
        console.log('📊 Browser Environment Diagnostics:', diagnosticInfo);
        console.log('📊 Full invoice object:', invoice);
        
        alert(`Diagnostics run - check console for details.
Origin: ${diagnosticInfo.origin}
Invoice: ${diagnosticInfo.invoice}
EmailId: ${diagnosticInfo.emailId}
Cookies: ${diagnosticInfo.cookies}`);
    };

    if (errorMessage) return <p>{errorMessage}</p>;
    if (!invoice) return <p>Loading...</p>;    // Extract client name for display
    const clientName = invoice?.clientName || invoice?.Client?.name || invoice?.payee || 'Unknown Client';
    
    // Find any matching client portal URLs from our configuration
    const matchingClientPortal = CLIENT_PORTAL_URLS.find(entry => 
        entry.match(clientName)
    );
    
    return (
        <div className="invoice-details">
            {/* Header Section */}
            <div className="invoice-header">
                <div className="invoice-header-content">
                    <button onClick={handleGoBack} className="back-btn">
                        <span className="back-icon">←</span>
                        Back to Invoices
                    </button>
                    <div className="client-badge">
                        <span className="client-badge-icon">🏢</span>
                        {clientName}
                    </div>
                </div>
            </div>

            <div className="invoice-container">
                {/* Left Column */}
                <div className="invoice-left-column">
                    {/* Invoice Info Card */}
                    <div className="invoice-info-card">
                        <div className="invoice-info-header">
                            <h2>Invoice Details</h2>
                        </div>
                        <div className="invoice-info-body">
                            {/* Duplicate Source Banner */}
                            {invoice.duplicateSource && (
                                <DuplicateSourceBanner duplicateSource={invoice.duplicateSource} emailId={invoice.emailId} />
                            )}

                            {/* Portal Buttons */}
                            <div className="portal-buttons">
                                <button onClick={openAppFolioUrl} className="portal-btn appfolio">
                                    <span>🏠</span> Go to AppFolio
                                </button>
                                {matchingClientPortal && (
                                    <a
                                        href={matchingClientPortal.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="portal-btn client-portal"
                                    >
                                        <span>🔗</span> {matchingClientPortal.label}
                                    </a>
                                )}
                            </div>
                    
                            {/* Invoice Fields */}
                            {!isEditing ? (
                                <>
                                    <div className="invoice-fields">
                                        <div className="invoice-field">
                                            <div className="field-content">
                                                <span className="field-label">Invoice Number</span>
                                                <span className={`field-value ${!invoice.invoiceNumber ? 'empty' : ''}`}>
                                                    {invoice.invoiceNumber || "Empty"}
                                                </span>
                                            </div>
                                            <button className="copy-btn" onClick={() => copyToClipboard(invoice.invoiceNumber || "Empty")}>
                                                Copy
                                            </button>
                                        </div>
                                        <div className="invoice-field">
                                            <div className="field-content">
                                                <span className="field-label">Page Number</span>
                                                <span className="field-value">{invoice.pageNumber || "1"}</span>
                                            </div>
                                            <button className="copy-btn" onClick={() => copyToClipboard(invoice.pageNumber?.toString() || "1")}>
                                                Copy
                                            </button>
                                        </div>
                                        <div className="invoice-field">
                                            <div className="field-content">
                                                <span className="field-label">Payee</span>
                                                <span className={`field-value ${!invoice.payee ? 'empty' : ''}`}>
                                                    {invoice.payee || "Empty"}
                                                </span>
                                            </div>
                                            <button className="copy-btn" onClick={() => copyToClipboard(invoice.payee || "Empty")}>
                                                Copy
                                            </button>
                                        </div>
                                        <div className="invoice-field">
                                            <div className="field-content">
                                                <span className="field-label">Total Amount</span>
                                                <span className={`field-value highlight ${!invoice.totalAmount ? 'empty' : ''}`}>
                                                    {invoice.totalAmount || "Empty"}
                                                </span>
                                            </div>
                                            <button className="copy-btn" onClick={() => copyToClipboard(invoice.totalAmount || "Empty")}>
                                                Copy
                                            </button>
                                        </div>
                                        <div className="invoice-field">
                                            <div className="field-content">
                                                <span className="field-label">Bill Date</span>
                                                <span className={`field-value ${!invoice.billDate ? 'empty' : ''}`}>
                                                    {invoice.billDate || "Empty"}
                                                </span>
                                            </div>
                                            <button className="copy-btn" onClick={() => copyToClipboard(invoice.billDate || "Empty")}>
                                                Copy
                                            </button>
                                        </div>
                                        <div className="invoice-field">
                                            <div className="field-content">
                                                <span className="field-label">Due Date</span>
                                                <span className={`field-value ${!invoice.dueDate ? 'empty' : ''}`}>
                                                    {invoice.dueDate || "Empty"}
                                                </span>
                                            </div>
                                            <button className="copy-btn" onClick={() => copyToClipboard(invoice.dueDate || "Empty")}>
                                                Copy
                                            </button>
                                        </div>
                                        <div className="invoice-field">
                                            <div className="field-content">
                                                <span className="field-label">Purchase Order</span>
                                                <span className={`field-value ${!invoice.purchaseOrder ? 'empty' : ''}`}>
                                                    {invoice.purchaseOrder || "Empty"}
                                                </span>
                                            </div>
                                            <button className="copy-btn" onClick={() => copyToClipboard(invoice.purchaseOrder || "Empty")}>
                                                Copy
                                            </button>
                                        </div>
                                        <div className="invoice-field">
                                            <div className="field-content">
                                                <span className="field-label">Work Order</span>
                                                <span className={`field-value ${!invoice.workOrder ? 'empty' : ''}`}>
                                                    {invoice.workOrder || "Empty"}
                                                </span>
                                            </div>
                                            <button className="copy-btn" onClick={() => copyToClipboard(invoice.workOrder || "Empty")}>
                                                Copy
                                            </button>
                                        </div>
                                        <div className="invoice-field">
                                            <div className="field-content">
                                                <span className="field-label">Reference</span>
                                                <span className={`field-value ${!invoice.reference ? 'empty' : ''}`}>
                                                    {invoice.reference || "Empty"}
                                                </span>
                                            </div>
                                            <button className="copy-btn" onClick={() => copyToClipboard(invoice.reference || "Empty")}>
                                                Copy
                                            </button>
                                        </div>
                                        <div className="invoice-field">
                                            <div className="field-content">
                                                <span className="field-label">Remarks</span>
                                                <span className={`field-value ${!invoice.remarks ? 'empty' : ''}`}>
                                                    {invoice.remarks || "Empty"}
                                                </span>
                                            </div>
                                            <button className="copy-btn" onClick={() => copyToClipboard(invoice.remarks || "Empty")}>
                                                Copy
                                            </button>
                                        </div>
                                        <div className="invoice-field">
                                            <div className="field-content">
                                                <span className="field-label">Cash Account</span>
                                                <span className={`field-value ${!invoice.cashAccount ? 'empty' : ''}`}>
                                                    {invoice.cashAccount || "Empty"}
                                                </span>
                                            </div>
                                            <button className="copy-btn" onClick={() => copyToClipboard(invoice.cashAccount || "Empty")}>
                                                Copy
                                            </button>
                                        </div>
                                        <div className="invoice-field">
                                            <div className="field-content">
                                                <span className="field-label">Memo for Check</span>
                                                <span className={`field-value ${!invoice.memoForCheck ? 'empty' : ''}`}>
                                                    {invoice.memoForCheck || "Empty"}
                                                </span>
                                            </div>
                                            <button className="copy-btn" onClick={() => copyToClipboard(invoice.memoForCheck || "Empty")}>
                                                Copy
                                            </button>
                                        </div>
                                        <div className="invoice-field">
                                            <div className="field-content">
                                                <span className="field-label">Property</span>
                                                <span className={`field-value ${!invoice.property ? 'empty' : ''}`}>
                                                    {invoice.property || "Empty"}
                                                </span>
                                            </div>
                                            <button className="copy-btn" onClick={() => copyToClipboard(invoice.property || "Empty")}>
                                                Copy
                                            </button>
                                        </div>
                                        <div className="invoice-field">
                                            <div className="field-content">
                                                <span className="field-label">Unit</span>
                                                <span className={`field-value ${!invoice.unit ? 'empty' : ''}`}>
                                                    {invoice.unit || "Empty"}
                                                </span>
                                            </div>
                                            <button className="copy-btn" onClick={() => copyToClipboard(invoice.unit || "Empty")}>
                                                Copy
                                            </button>
                                        </div>
                                        <div className="invoice-field">
                                            <div className="field-content">
                                                <span className="field-label">GL Account</span>
                                                <span className={`field-value ${!invoice.glAccount ? 'empty' : ''}`}>
                                                    {invoice.glAccount || "Empty"}
                                                </span>
                                            </div>
                                            <button className="copy-btn" onClick={() => copyToClipboard(invoice.glAccount || "Empty")}>
                                                Copy
                                            </button>
                                        </div>
                                        <div className="invoice-field">
                                            <div className="field-content">
                                                <span className="field-label">Description</span>
                                                <span className={`field-value ${!invoice.description ? 'empty' : ''}`}>
                                                    {invoice.description || "Empty"}
                                                </span>
                                            </div>
                                            <button className="copy-btn" onClick={() => copyToClipboard(invoice.description || "Empty")}>
                                                Copy
                                            </button>
                                        </div>
                                        <div className="invoice-field">
                                            <div className="field-content">
                                                <span className="field-label">Total</span>
                                                <span className={`field-value highlight ${!invoice.total ? 'empty' : ''}`}>
                                                    {invoice.total || "Empty"}
                                                </span>
                                            </div>
                                            <button className="copy-btn" onClick={() => copyToClipboard(invoice.total || "Empty")}>
                                                Copy
                                            </button>
                                        </div>
                                        <div className="invoice-field">
                                            <div className="field-content">
                                                <span className="field-label">Marked as Paid</span>
                                                <span className="field-value">{invoice.markedAsPaid ? "Yes" : "No"}</span>
                                            </div>
                                            <button className="copy-btn" onClick={() => copyToClipboard(invoice.markedAsPaid ? "Yes" : "No")}>
                                                Copy
                                            </button>
                                        </div>
                                        {invoice.senderEmail && (
                                            <div className="invoice-field">
                                                <div className="field-content">
                                                    <span className="field-label">Sender Email</span>
                                                    <span className="field-value">{invoice.senderEmail}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {/* Line Items Section */}
                                    {invoice.lineItems && invoice.lineItems.length > 0 && (
                                        <div className="line-items-section">
                                            <h3 className="line-items-title">Line Items</h3>
                                            {invoice.lineItems.map((item, index) => (
                                                <div key={index} className="line-item-card">
                                                    <div className="invoice-field">
                                                        <div className="field-content">
                                                            <span className="field-label">Property</span>
                                                            <span className={`field-value ${!item.property ? 'empty' : ''}`}>
                                                                {item.property || "Not Found"}
                                                            </span>
                                                        </div>
                                                        <button className="copy-btn" onClick={() => copyToClipboard(item.property || "Not Found")}>
                                                            Copy
                                                        </button>
                                                    </div>
                                                    <div className="invoice-field">
                                                        <div className="field-content">
                                                            <span className="field-label">Unit</span>
                                                            <span className={`field-value ${!item.unit ? 'empty' : ''}`}>
                                                                {item.unit || "Not Found"}
                                                            </span>
                                                        </div>
                                                        <button className="copy-btn" onClick={() => copyToClipboard(item.unit || "Not Found")}>
                                                            Copy
                                                        </button>
                                                    </div>
                                                    <div className="invoice-field">
                                                        <div className="field-content">
                                                            <span className="field-label">GL Account</span>
                                                            <span className={`field-value ${!item.glAccount ? 'empty' : ''}`}>
                                                                {item.glAccount || "Not Found"}
                                                            </span>
                                                        </div>
                                                        <button className="copy-btn" onClick={() => copyToClipboard(item.glAccount || "Not Found")}>
                                                            Copy
                                                        </button>
                                                    </div>
                                                    <div className="invoice-field">
                                                        <div className="field-content">
                                                            <span className="field-label">Description</span>
                                                            <span className={`field-value ${!item.description ? 'empty' : ''}`}>
                                                                {item.description || "Not Found"}
                                                            </span>
                                                        </div>
                                                        <button className="copy-btn" onClick={() => copyToClipboard(item.description || "Not Found")}>
                                                            Copy
                                                        </button>
                                                    </div>
                                                    <div className="invoice-field">
                                                        <div className="field-content">
                                                            <span className="field-label">Amount</span>
                                                            <span className={`field-value highlight ${!item.amount ? 'empty' : ''}`}>
                                                                {item.amount || "Not Found"}
                                                            </span>
                                                        </div>
                                                        <button className="copy-btn" onClick={() => copyToClipboard(item.amount || "Not Found")}>
                                                            Copy
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {/* Action Buttons */}
                                    <div className="action-buttons">
                                        <button onClick={() => setIsEditing(true)} className="action-btn edit">
                                            <span>✏️</span> Edit Invoice
                                        </button>
                                        <ToggleReviewButton
                                            invoiceId={invoice.id}
                                            reviewed={invoice.reviewed}
                                            onReviewToggled={(updatedInvoice) => {
                                                setInvoice((prevInvoice) => ({
                                                    ...prevInvoice,
                                                    ...updatedInvoice,
                                                }));
                                            }}
                                        />
                                        {invoice && invoice.emailId && (
                                            <a
                                                href={`/email/${invoice.emailId}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="action-btn email"
                                            >
                                                <span>📧</span> Open Email Details
                                            </a>
                                        )}
                                        {(!invoice?.emailId) && (
                                            <button
                                                className="action-btn associate"
                                                onClick={async () => {
                                                    try {
                                                        const data = await associationService.createAssociations();
                                                        if (data.success) {
                                                            alert(`Created ${data.associationsCreated} associations. Refreshing page to show updated data.`);
                                                            window.location.reload();
                                                        } else {
                                                            alert(`Failed to find associations: ${data.message}`);
                                                        }
                                                    } catch (error) {
                                                        alert(`Error finding associations: ${error.message}`);
                                                    }
                                                }}
                                            >
                                                <span>🔗</span> Find Email Association
                                            </button>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <form onSubmit={handleFormSubmit} className="edit-form">
                                    <label>Invoice Number:
                                        <input type="text" name="invoiceNumber" value={formData.invoiceNumber || ""} onChange={handleInputChange} />
                                    </label>
                                    <label>Page Number:
                                        <input type="number" name="pageNumber" value={formData.pageNumber || ""} onChange={handleInputChange} min="1" />
                                    </label>
                                    <label>Payee:
                                        <textarea name="payee" value={formData.payee || ""} onChange={handleInputChange} />
                                    </label>
                                    <label>Total Amount:
                                        <input type="text" name="totalAmount" value={formData.totalAmount || ""} onChange={handleInputChange} />
                                    </label>
                                    <label>Bill Date:
                                        <input type="date" name="billDate" value={formData.billDate || ""} onChange={handleInputChange} />
                                    </label>
                                    <label>Due Date:
                                        <input type="date" name="dueDate" value={formData.dueDate || ""} onChange={handleInputChange} />
                                    </label>
                                    <label>Purchase Order:
                                        <textarea name="purchaseOrder" value={formData.purchaseOrder || ""} onChange={handleInputChange} />
                                    </label>
                                    <label>Work Order:
                                        <textarea name="workOrder" value={formData.workOrder || ""} onChange={handleInputChange} />
                                    </label>
                                    <label>Reference:
                                        <textarea name="reference" value={formData.reference || ""} onChange={handleInputChange} />
                                    </label>
                                    <label>Remarks:
                                        <textarea name="remarks" value={formData.remarks || ""} onChange={handleInputChange} />
                                    </label>
                                    <label>Cash Account:
                                        <textarea name="cashAccount" value={formData.cashAccount || ""} onChange={handleInputChange} />
                                    </label>
                                    <label>Memo for Check:
                                        <textarea name="memoForCheck" value={formData.memoForCheck || ""} onChange={handleInputChange} />
                                    </label>
                                    <label>Property:
                                        <textarea name="property" value={formData.property || ""} onChange={handleInputChange} />
                                    </label>
                                    <label>Unit:
                                        <textarea name="unit" value={formData.unit || ""} onChange={handleInputChange} />
                                    </label>
                                    <label>GL Account:
                                        <textarea name="glAccount" value={formData.glAccount || ""} onChange={handleInputChange} />
                                    </label>
                                    <label>Description:
                                        <textarea name="description" value={formData.description || ""} onChange={handleInputChange} />
                                    </label>
                                    <label>Total:
                                        <input type="text" name="total" value={formData.total || ""} onChange={handleInputChange} />
                                    </label>
                                    <label>Marked as Paid:
                                        <input type="checkbox" name="markedAsPaid" checked={formData.markedAsPaid || false} onChange={(e) => setFormData({ ...formData, markedAsPaid: e.target.checked })} />
                                    </label>
                                    <label>Reviewed:
                                        <input type="checkbox" name="reviewed" checked={formData.reviewed || false} onChange={(e) => setFormData({ ...formData, reviewed: e.target.checked })} />
                                    </label>

                                    {/* Line Items Edit */}
                                    <div className="line-items-section">
                                        <h3 className="line-items-title">Line Items</h3>
                                        {formData.lineItems && formData.lineItems.length > 0 ? (
                                            formData.lineItems.map((item, index) => (
                                                <div key={index} className="line-item-card">
                                                    <label>Property:
                                                        <input type="text" value={item.property || ""} onChange={(e) => handleLineItemChange(index, "property", e.target.value)} />
                                                    </label>
                                                    <label>Unit:
                                                        <input type="text" value={item.unit || ""} onChange={(e) => handleLineItemChange(index, "unit", e.target.value)} />
                                                    </label>
                                                    <label>GL Account:
                                                        <input type="text" value={item.glAccount || ""} onChange={(e) => handleLineItemChange(index, "glAccount", e.target.value)} />
                                                    </label>
                                                    <label>Description:
                                                        <textarea value={item.description || ""} onChange={(e) => handleLineItemChange(index, "description", e.target.value)} />
                                                    </label>
                                                    <label>Amount:
                                                        <input type="text" value={item.amount || ""} onChange={(e) => handleLineItemChange(index, "amount", e.target.value)} />
                                                    </label>
                                                    <button type="button" className="form-btn cancel" onClick={() => handleRemoveLineItem(index)}>
                                                        Remove Line Item
                                                    </button>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="no-comment-text">No line items found.</p>
                                        )}
                                        <button type="button" className="form-btn save" onClick={handleAddLineItem}>
                                            Add Line Item
                                        </button>
                                    </div>

                                    <label>Comment:
                                        <textarea name="comment" value={formData.comment || ""} onChange={handleInputChange} placeholder="Add a comment about this invoice..." />
                                    </label>

                                    <div className="form-actions">
                                        <button type="submit" className="form-btn save">💾 Save Changes</button>
                                        <button type="button" className="form-btn cancel" onClick={() => setIsEditing(false)}>Cancel</button>
                                    </div>
                                </form>
                    )}

                            {/* Email details section - always show if associatedEmail is available */}
                            {showEmailInfo && associatedEmail && (
                                <div className="email-section">
                                    <EmailListView
                                        title="Associated Email"
                                        emails={[associatedEmail]}
                                        onMarkAsReviewed={() => {}}
                                        onAddComment={() => {}}
                                        onUpdateComment={() => {}}
                                        currentPage={1}
                                        totalPages={1}
                                        onPageChange={() => {}}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Comment Section Card */}
                    <div className="comment-section-card">
                        <div className="comment-section-header">
                            <h3>Comment</h3>
                        </div>
                        <div className="comment-section-body">
                            {isEditingComment ? (
                                <div className="edit-comment-form">
                                    <textarea
                                        value={commentText}
                                        onChange={(e) => setCommentText(e.target.value)}
                                        className="comment-textarea"
                                        placeholder="Add a comment..."
                                    />
                                    <div className="comment-actions">
                                        <button onClick={handleCommentUpdate} className="comment-btn save">
                                            💾 Save
                                        </button>
                                        <button
                                            onClick={() => {
                                                setIsEditingComment(false);
                                                setCommentText(invoice.comment || "");
                                            }}
                                            className="comment-btn cancel"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="invoice-comment">
                                    {invoice.comment ? (
                                        <>
                                            <div className="comment-display" dangerouslySetInnerHTML={{ __html: linkify(invoice.comment) }} />
                                            <button
                                                onClick={() => {
                                                    setCommentText(invoice.comment || "");
                                                    setIsEditingComment(true);
                                                }}
                                                className="comment-btn edit"
                                            >
                                                ✏️ Edit Comment
                                            </button>
                                        </>
                                    ) : (
                                        <div className="no-comment">
                                            <p className="no-comment-text">No comments yet</p>
                                            <button
                                                onClick={() => {
                                                    setCommentText("");
                                                    setIsEditingComment(true);
                                                }}
                                                className="comment-btn add"
                                            >
                                                ➕ Add Comment
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* PDF Viewer Card */}
                <div className="pdf-viewer-card">
                    <div className="pdf-viewer-header">
                        <h3>
                            PDF Document
                            {invoice.pdfFile && (() => {
                                const base64Length = invoice.pdfFile.length;
                                const pdfSizeBytes = Math.floor(base64Length * 3 / 4);
                                const pdfSizeMB = (pdfSizeBytes / (1024 * 1024));
                                return <span className="pdf-size-badge">{pdfSizeMB.toFixed(2)} MB</span>;
                            })()}
                        </h3>
                    </div>
                    <div className="pdf-viewer-body">
                        {invoice.pdfFile ? (() => {
                            const base64Length = invoice.pdfFile.length;
                            const pdfSizeBytes = Math.floor(base64Length * 3 / 4);
                            const pdfSizeMB = (pdfSizeBytes / (1024 * 1024));
                            const showWarning = pdfSizeMB > 2;
                            return (
                                <>
                                    {showWarning && (
                                        <div className="pdf-warning">
                                            <span className="pdf-warning-icon">⚠️</span>
                                            <span>Large PDF - If viewing issues occur, please download and open with a PDF reader.</span>
                                        </div>
                                    )}
                                    <button
                                        className="download-btn"
                                        onClick={() => {
                                            try {
                                                const link = document.createElement('a');
                                                link.href = `data:application/pdf;base64,${invoice.pdfFile}`;
                                                link.download = `invoice.pdf`;
                                                document.body.appendChild(link);
                                                link.click();
                                                document.body.removeChild(link);
                                            } catch (err) {
                                                console.error('❌ Error downloading PDF:', err);
                                            }
                                        }}
                                    >
                                        <span className="download-icon">⬇️</span>
                                        Download PDF
                                    </button>
                                    <div className="pdf-embed-container">
                                        <iframe
                                            src={`data:application/pdf;base64,${invoice.pdfFile}`}
                                            className="pdf-iframe"
                                            title="Invoice PDF"
                                        />
                                    </div>
                                </>
                            );
                        })() : (
                            <p className="no-pdf-message">No PDF available for this invoice.</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InvoiceDetails;
